import { create, v, w } from '@dojo/framework/core/vdom';
import { theme } from '@dojo/framework/core/middleware/theme';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import has from '@dojo/framework/core/has';
import { Keys } from '@holmescorp/common';
import { lccstate, createNlsMiddleware } from '@holmescorp/lcc-state';
import css from '../css/you-are-here.m.css';
import { DashboardWithFocus } from '@holmescorp/dashboard';
import { RecommendedNextSteps, Praise, ProgressTracker, DomainTabcontainer, PathChoice } from '@holmescorp/dashboard-widgets';
import { isEmpty } from 'lodash-es';
const nls = createNlsMiddleware('ditabook');
const nlsSettings = createNlsMiddleware('settings');
const factory = create({
    theme,
    nls,
    nlsSettings,
    lccstate
}).properties();
export const YouAreHere = factory(({ properties, middleware: { theme, nls, nlsSettings, lccstate } }) => {
    const useAssessView = !has('no-explore-assess');
    const useStudyView = !has('no-explore-study');
    const usePassView = !has('no-explore-pass');
    const viewcount = [useAssessView, useStudyView, usePassView]
        .map((value) => {
        return value ? 1 : 0;
    })
        .reduce((accum, curval) => {
        accum = accum + curval;
        return accum;
    }, 0);
    const { active, switchView } = properties();
    const themeCss = theme.classes(css);
    const { messages: { assess_title = '[ditabook [assess_title]', assess_text = 'Assess', assess_number = '1', study_title = '[ditabook [study_title]', study_text = 'Study', study_number = '2', practice_title = '[ditabook [practice_title]', practice_text = 'Practice', practice_number = '3' } } = nls();
    const { showDashboard = 'false' } = nlsSettings().messages;
    const convertToBoolean = (s) => {
        try {
            return JSON.parse(s);
        }
        catch (e) {
            return false;
        }
    };
    const buttonIcons = [assess_number, study_number, practice_number].map((step) => {
        return v('span', { classes: ['fa-stack', 'fa-2x'] }, [
            v('i', { classes: ['far', 'fa-circle', 'fa-stack-2x'] }),
            v('strong', { classes: ['fa-stack-1x'] }, [step])
        ]);
    });
    const setTitle = (title) => {
        if (title) {
            document.title = title;
        }
    };
    setTitle(nls().messages[`${active}_title`]);
    const assessSelected = active === 'assess';
    const studySelected = active === 'study';
    const practiceSelected = active === 'practice';
    const sdb = convertToBoolean(showDashboard);
    const { get: lget, path: lpath } = lccstate;
    const quizpatterns = lget(lpath('quizpatterns'));
    const emptyQP = { '': '' };
    const hasQP = !isEmpty(quizpatterns) && JSON.stringify(quizpatterns) !== JSON.stringify(emptyQP);
    const dashboardKids = [
        w(Praise, {}),
        w(ProgressTracker, {}),
        w(RecommendedNextSteps, {}),
        w(DomainTabcontainer, {}),
        hasQP ? w(PathChoice, {}) : null
    ];
    return v('div', {
        role: 'tablist',
        'aria-orientation': 'horizontal',
        key: 'button-group',
        classes: ['you-are-here', themeCss.buttonGroup]
    }, [
        useAssessView
            ? v('div', {
                id: 'assessTab',
                role: 'tab',
                ...formatAriaProperties({
                    selected: assessSelected ? 'true' : 'false',
                    controls: 'tabPanelAssess'
                }),
                classes: [themeCss.buttonWrapper, assessSelected ? themeCss.active : null],
                tabIndex: 0,
                onclick: (event) => {
                    event.stopPropagation();
                    if (!assessSelected) {
                        setTitle(assess_title);
                        switchView('assess');
                    }
                },
                onkeydown: (event) => {
                    switch (event.key) {
                        case Keys.Enter:
                        case Keys.Space:
                        case Keys.Spacebar:
                            event.preventDefault();
                            if (!assessSelected) {
                                setTitle(assess_title);
                                switchView('assess');
                            }
                    }
                }
            }, viewcount > 1
                ? [
                    v('div', {}, [
                        buttonIcons[0],
                        v('span', { classes: [themeCss.title] }, [assess_text]),
                        v('div', { classes: [themeCss.arrow] })
                    ])
                ]
                : [])
            : null,
        useStudyView
            ? v('div', {
                id: 'studyTab',
                role: 'tab',
                ...formatAriaProperties({
                    selected: studySelected ? 'true' : 'false',
                    controls: 'tabPanelStudy'
                }),
                classes: [themeCss.buttonWrapper, studySelected ? themeCss.active : null],
                tabIndex: 0,
                onclick: (event) => {
                    event.stopPropagation();
                    if (!studySelected) {
                        setTitle(study_title);
                        switchView('study');
                    }
                },
                onkeydown: (event) => {
                    switch (event.key) {
                        case Keys.Enter:
                        case Keys.Space:
                        case Keys.Spacebar:
                            event.preventDefault();
                            if (!studySelected) {
                                setTitle(study_title);
                                switchView('study');
                            }
                    }
                }
            }, viewcount > 1
                ? [
                    v('div', {}, [
                        buttonIcons[1],
                        v('span', { classes: [themeCss.title] }, [study_text]),
                        v('div', { classes: [themeCss.arrow] })
                    ])
                ]
                : [])
            : null,
        usePassView
            ? v('div', {
                id: 'practiceTab',
                role: 'tab',
                ...formatAriaProperties({
                    selected: practiceSelected ? 'true' : 'false',
                    controls: 'tabPanelPractice'
                }),
                classes: [themeCss.buttonWrapper, practiceSelected ? themeCss.active : null],
                tabIndex: 0,
                onclick: (event) => {
                    event.stopPropagation();
                    if (!practiceSelected) {
                        setTitle(practice_title);
                        switchView('practice');
                    }
                },
                onkeydown: (event) => {
                    switch (event.key) {
                        case Keys.Enter:
                        case Keys.Space:
                        case Keys.Spacebar:
                            event.preventDefault();
                            if (!practiceSelected) {
                                setTitle(practice_title);
                                switchView('practice');
                            }
                    }
                }
            }, [
                v('div', {}, viewcount > 1
                    ? [
                        buttonIcons[2],
                        v('span', { classes: [themeCss.title] }, [practice_text]),
                        v('div', { classes: [themeCss.arrow] })
                    ]
                    : [])
            ])
            : null,
        sdb ? w(DashboardWithFocus, {}, dashboardKids) : null
    ]);
});
